import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FlaskConical, Power } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
const toLocal = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16) : "");
const fromLocal = (v) => (v ? new Date(`${v}:00Z`).toISOString() : null);

function VariantEditor({ v, onChange }) {
  return (
    <div className="border border-zinc-200 rounded-lg p-3 space-y-2" data-testid={`exp-variant-${v.id}`}>
      <div className="flex items-center gap-3">
        <span className="w-7 h-7 rounded-full bg-zinc-900 text-white text-xs font-bold flex items-center justify-center">{v.id}</span>
        {v.id === "A" && <span className="text-[10px] uppercase tracking-wide text-zinc-500">Control</span>}
        <label className="ml-auto flex items-center gap-2 text-xs text-zinc-600">
          Weight
          <Input type="number" min="0" value={v.weight} className="w-20 h-8"
            data-testid={`exp-weight-${v.id}`} onChange={(e) => onChange({ ...v, weight: Number(e.target.value) })} />
        </label>
      </div>
      <Input value={v.headline} placeholder="Headline" data-testid={`exp-headline-${v.id}`}
        onChange={(e) => onChange({ ...v, headline: e.target.value })} />
      <Textarea value={v.subhead} rows={2} placeholder="Subhead (optional)" data-testid={`exp-subhead-${v.id}`}
        onChange={(e) => onChange({ ...v, subhead: e.target.value })} />
    </div>
  );
}

export function HeadlineExperimentConfig({ expKey, onSaved }) {
  const [exp, setExp] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/experiments/${expKey}`, auth()).then((r) => { setExp(r.data); setSaved(r.data); }).catch(() => toast.error("Failed to load experiment"));
  }, [expKey]);

  // Kill switch saves only `enabled` on top of the last saved config; unsaved edits stay in the form.
  const save = async (toggle) => {
    const next = toggle === undefined ? exp : { ...saved, enabled: toggle };
    setSaving(true);
    try {
      const r = await axios.put(`${API}/experiments/${expKey}`, {
        enabled: next.enabled, variants: next.variants, start_at: next.start_at || null, end_at: next.end_at || null,
      }, auth());
      setSaved(r.data);
      setExp(toggle === undefined ? r.data : { ...exp, enabled: r.data.enabled, live: r.data.live, audit: r.data.audit });
      toast.success(toggle === undefined ? "Experiment saved" : `Experiment turned ${toggle ? "ON" : "OFF"}`);
      onSaved && onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? `Check variant fields: ${d.map((x) => x.msg).join("; ")}` : d || "Save failed");
    } finally { setSaving(false); }
  };

  if (!exp) return <div className="h-24 animate-pulse bg-zinc-100 rounded-lg" />;
  const totalW = exp.variants.reduce((s, v) => s + Number(v.weight || 0), 0);
  const setVariant = (nv) => setExp({ ...exp, variants: exp.variants.map((v) => (v.id === nv.id ? nv : v)) });

  return (
    <div className="space-y-4" data-testid="headline-experiment-config">
      <div className="flex items-center gap-3 flex-wrap">
        <FlaskConical className="w-5 h-5 text-[#207024]" />
        <h2 className="text-lg font-semibold text-zinc-900">Instant Estimate headline test</h2>
        <code className="text-xs bg-zinc-100 px-2 py-0.5 rounded">{expKey}</code>
        <span data-testid="exp-live-status" className={`text-xs font-semibold px-2 py-0.5 rounded-full ${exp.live ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-600"}`}>
          {exp.live ? "LIVE" : "OFF — everyone sees A"}
        </span>
        <label className="ml-auto flex items-center gap-2 text-sm font-medium">
          <Power className="w-4 h-4" /> Kill switch
          <Switch checked={exp.enabled} disabled={saving} data-testid="exp-enabled-switch"
            onCheckedChange={(c) => save(c)} />
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {exp.variants.map((v) => <VariantEditor key={v.id} v={v} onChange={setVariant} />)}
      </div>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <label className="space-y-1"><span className="block text-xs text-zinc-500">Start (UTC)</span>
          <Input type="datetime-local" value={toLocal(exp.start_at)} data-testid="exp-start-at"
            onChange={(e) => setExp({ ...exp, start_at: fromLocal(e.target.value) })} /></label>
        <label className="space-y-1"><span className="block text-xs text-zinc-500">End (UTC, optional)</span>
          <Input type="datetime-local" value={toLocal(exp.end_at)} data-testid="exp-end-at"
            onChange={(e) => setExp({ ...exp, end_at: fromLocal(e.target.value) })} /></label>
        <span className="text-xs text-zinc-500 pb-2">Split: {exp.variants.map((v) => `${v.id} ${totalW ? Math.round((v.weight / totalW) * 100) : 0}%`).join(" · ")}</span>
        <Button className="ml-auto bg-[#207024] hover:bg-[#185a1c]" disabled={saving} onClick={() => save()} data-testid="exp-save-btn">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
      {exp.audit?.length > 0 && (
        <details className="text-xs text-zinc-600" data-testid="exp-audit-log">
          <summary className="cursor-pointer font-medium">Change log ({exp.audit.length})</summary>
          <ul className="mt-2 space-y-1">
            {exp.audit.map((a, i) => (
              <li key={i}>{new Date(a.at).toLocaleString()} — {a.user_name || a.user_email}: {a.changed?.join(", ") || "no changes"}
                {a.enabled_before !== a.enabled_after && <strong> (turned {a.enabled_after ? "ON" : "OFF"})</strong>}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
